// B5-DECIDE (universal-task plan §b.6): the cheap classifier as a tool the DE calls.
//
//   POST runs/{id}/decide                 the run (MCP `decide`): classify up to 100 items against
//                                         one of the DE's rubrics (or questions given inline);
//                                         per item and question the distribution, the top option,
//                                         its probability and the band (act / review / unsure).
//                                         Logged per answer, cost booked on the run's seat.
//   GET  runs/{id}/decision-rubrics       the run's DE: its rubrics (newest version of each)
//   POST runs/{id}/decision-rubrics       the run's DE: save a rubric (a new version)
//   GET  decision-rubrics                 people: the rubrics of the DEs whose runs they may view
//   GET  decision-rubrics/{id}            people: one version, with the version list
//   POST decision-rubrics/{id}/versions   people: edit (a new version of that rubric)
//   GET  decisions                        people: the logged answers (calibration, Activity)
//   GET  decide/settings                  managers: the switches, and what is available
//   PUT  decide/settings                  managers: turn Jev (external) and the board gate on/off
//
// The provider is Jev (`typesafe/jev-1.13`, OpenRouter's alpha Decisions API) when the company
// switched `external` on and holds an OpenRouter key in Connections › API keys; else the local
// light model through the company's gateway. Item text is masked (e-mail addresses, phone numbers)
// before it leaves for Jev; files are never sent.
import { z } from "zod";
import { ClassifierDecisionSource, DecisionBand, DecisionProvider } from "../enums";
import { boundedRecord, Timestamp, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const agents = { feature: "agents" } as const;

export const DECIDE_MAX_ITEMS = 100;
export const DECIDE_MAX_QUESTIONS = 10;
/** One item's text, at most (the classifier's context is 32k tokens). */
export const DECIDE_MAX_STATE_CHARS = 24_000;
export const DECIDE_DEFAULT_ACT = 0.7;
export const DECIDE_DEFAULT_REVIEW = 0.35;

const Key = z
  .string()
  .max(64)
  .regex(/^[a-z][a-z0-9_]{0,63}$/, "lowercase letters, digits and _ (a-z first), up to 64");
const OptionKey = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(/^[^\s][^\r\n]*$/, "one line");
const Line = (max: number) =>
  z
    .string()
    .trim()
    .min(1)
    .max(max)
    .regex(/^[^\r\n]*$/, "one line");

export const DecisionRubricOption = z
  .strictObject({
    key: OptionKey,
    /** What the option is for (the rubric's main lever). */
    what: z.string().trim().min(1).max(1000),
    /** What it is NOT for (the near misses). */
    not_for: z.string().trim().min(1).max(1000).optional(),
    examples: z.array(z.string().trim().min(1).max(500)).max(8).optional(),
  })
  .meta({ id: "DecisionRubricOption" });

export const DecisionRubricQuestion = z
  .strictObject({
    key: Key,
    type: z.enum(["boolean", "choice", "score"]),
    question: z.string().trim().min(1).max(1000),
    /** The policy line that steers the answer ("route to whoever must fix the root cause"). */
    focus: z.string().trim().min(1).max(500).optional(),
    /** `boolean`: exactly `true` and `false`; `score`: ordered, lowest first. */
    options: z.array(DecisionRubricOption).min(2).max(20),
  })
  .superRefine((q, ctx) => {
    const keys = q.options.map((o) => o.key);
    if (new Set(keys).size !== keys.length)
      ctx.addIssue({ code: "custom", path: ["options"], message: "option keys must differ" });
    if (keys.some((k) => ["__proto__", "constructor", "prototype"].includes(k)))
      ctx.addIssue({ code: "custom", path: ["options"], message: "that option key is reserved" });
    // Jev's score scale has 2 to 10 levels.
    if (q.type === "score" && keys.length > 10)
      ctx.addIssue({ code: "custom", path: ["options"], message: "a score has at most 10 levels" });
    if (
      q.type === "boolean" &&
      (keys.length !== 2 || !keys.includes("true") || !keys.includes("false"))
    )
      ctx.addIssue({
        code: "custom",
        path: ["options"],
        message: "a boolean question has exactly the options true and false",
      });
  })
  .meta({ id: "DecisionRubricQuestion" });
export type DecisionRubricQuestion = z.infer<typeof DecisionRubricQuestion>;

const Questions = z
  .array(DecisionRubricQuestion)
  .min(1)
  .max(DECIDE_MAX_QUESTIONS)
  .refine((qs) => new Set(qs.map((q) => q.key)).size === qs.length, "question keys must differ");

const Thresholds = {
  act_threshold: z.number().gt(0).max(1).optional(),
  review_threshold: z.number().gt(0).lt(1).optional(),
};
const thresholdsOk = (v: {
  act_threshold?: number | undefined;
  review_threshold?: number | undefined;
}) => (v.review_threshold ?? DECIDE_DEFAULT_REVIEW) < (v.act_threshold ?? DECIDE_DEFAULT_ACT);

/** Saving a rubric (a DE through its run, or a person's edit): always a new version. */
export const DecisionRubricSave = z
  .strictObject({
    key: Key,
    title: Line(120),
    focus: z.string().trim().min(1).max(500).optional(),
    questions: Questions,
    ...Thresholds,
    /** What changed, in a line. */
    note: Line(300).optional(),
  })
  // A partial threshold change is checked against the saved ones by the server (Codex r1).
  .meta({ id: "DecisionRubricSave" });
export type DecisionRubricSave = z.infer<typeof DecisionRubricSave>;

/** A person's edit of a rubric: the key stays the rubric's. */
export const DecisionRubricEdit = z
  .strictObject({
    title: Line(120),
    focus: z.string().trim().min(1).max(500).nullable().optional(),
    questions: Questions,
    ...Thresholds,
    note: Line(300).optional(),
  })
  // A partial threshold change is checked against the saved ones by the server (Codex r1).
  .meta({ id: "DecisionRubricEdit" });
export type DecisionRubricEdit = z.infer<typeof DecisionRubricEdit>;

export const DecisionRubric = z
  .object({
    id: Uuid,
    membership_id: Uuid,
    agent_name: z.string().nullable(),
    seat_id: Uuid.nullable(),
    key: z.string(),
    version: z.int().min(1),
    title: z.string(),
    focus: z.string().nullable(),
    questions: z.array(DecisionRubricQuestion),
    act_threshold: z.number(),
    review_threshold: z.number(),
    note: z.string().nullable(),
    /** Who saved this version: the DE itself, or a person. */
    saved_by: z.object({ kind: z.string().nullable(), membership_id: Uuid.nullable() }),
    created_at: Timestamp,
    /** Answers logged against this version. */
    decisions: z.int().min(0),
  })
  .meta({ id: "DecisionRubric" });
export type DecisionRubric = z.infer<typeof DecisionRubric>;

export const DecisionRubricVersion = z
  .object({
    id: Uuid,
    version: z.int().min(1),
    note: z.string().nullable(),
    saved_by: z.object({ kind: z.string().nullable(), membership_id: Uuid.nullable() }),
    created_at: Timestamp,
  })
  .meta({ id: "DecisionRubricVersion" });

export const DecisionRubricDetail = DecisionRubric.extend({
  versions: z.array(DecisionRubricVersion),
  /** The newest version's id (an edit always builds on it). */
  current_id: Uuid,
}).meta({ id: "DecisionRubricDetail" });

export const DecisionRubricList = z
  .object({ data: z.array(DecisionRubric) })
  .meta({ id: "DecisionRubricList" });

export const DecisionRubricListQuery = z.object({
  membership_id: Uuid.optional().describe("One DE's rubrics"),
});

/** One item to classify: the caller's id (a task id, a comment id, a row number) and its text. */
export const DecideItem = z.strictObject({
  id: z.string().trim().min(1).max(200),
  state: z.union([
    z.string().min(1).max(DECIDE_MAX_STATE_CHARS),
    // A JSON object (a task's fields, a post with its results): sent as JSON text, held to the
    // same size as a string state.
    boundedRecord(z.unknown(), 50).refine(
      (v) => JSON.stringify(v).length <= DECIDE_MAX_STATE_CHARS,
      `at most ${DECIDE_MAX_STATE_CHARS} characters as JSON`,
    ),
  ]),
});

export const DecideRequest = z
  .strictObject({
    items: z.array(DecideItem).min(1).max(DECIDE_MAX_ITEMS),
    /** One of the DE's rubrics (its newest version) … */
    rubric_key: Key.optional(),
    /** … or questions given inline (not saved, logged without a rubric). */
    questions: Questions.optional(),
    focus: z.string().trim().min(1).max(500).optional(),
    ...Thresholds,
  })
  .superRefine((v, ctx) => {
    if ((v.rubric_key === undefined) === (v.questions === undefined))
      ctx.addIssue({
        code: "custom",
        path: ["rubric_key"],
        message: "send rubric_key or questions (one of them)",
      });
    if (v.rubric_key !== undefined && (v.focus || v.act_threshold || v.review_threshold))
      ctx.addIssue({
        code: "custom",
        path: ["focus"],
        message: "a rubric carries its own focus and thresholds",
      });
    if (!thresholdsOk(v))
      ctx.addIssue({
        code: "custom",
        path: ["review_threshold"],
        message: "review_threshold must be below act_threshold",
      });
    const ids = v.items.map((i) => i.id);
    if (new Set(ids).size !== ids.length)
      ctx.addIssue({ code: "custom", path: ["items"], message: "item ids must differ" });
  })
  .meta({ id: "DecideRequest" });
export type DecideRequest = z.infer<typeof DecideRequest>;

export const DecideAnswer = z
  .object({
    type: z.enum(["boolean", "choice", "score"]),
    /** The top option. */
    chosen: z.string(),
    /** Its probability. */
    confidence: z.number().min(0).max(1),
    band: DecisionBand,
    distribution: z.record(z.string(), z.number()),
    /** `score`: the expected value Σ p·i over the ordered options (0 = the first). */
    score: z.number().optional(),
  })
  .meta({ id: "DecideAnswer" });
export type DecideAnswer = z.infer<typeof DecideAnswer>;

export const DecideResult = z
  .object({
    call_id: Uuid,
    provider: DecisionProvider,
    model: z.string(),
    /** Why Jev was not used although the switch is on (it failed or answered off-schema). */
    fallback_reason: z.string().nullable(),
    rubric: z.object({ id: Uuid, key: z.string(), version: z.int() }).nullable(),
    act_threshold: z.number(),
    review_threshold: z.number(),
    results: z.array(
      z.object({
        item_id: z.string(),
        /** Who answered this item (a Jev failure falls back to the local model per item). */
        provider: DecisionProvider,
        answers: z.record(z.string(), DecideAnswer),
      }),
    ),
    /** Items no provider could answer (read them yourself). */
    unanswered: z.array(z.string()),
    /** Answers per band: read the `review` ones yourself, ask a person about the `unsure` ones. */
    counts: z.object({ act: z.int(), review: z.int(), unsure: z.int() }),
    tokens_in: z.int().min(0),
    cost_micros: z.int().min(0),
    /** E-mail addresses and phone numbers masked before the text left the company. */
    masked: z.int().min(0),
  })
  .meta({ id: "DecideResult" });
export type DecideResult = z.infer<typeof DecideResult>;

export const ClassifierDecisionRow = z
  .object({
    id: Uuid,
    call_id: Uuid,
    source: ClassifierDecisionSource,
    membership_id: Uuid,
    run_id: Uuid.nullable(),
    rubric_id: Uuid.nullable(),
    rubric_version: z.int().nullable(),
    item_id: z.string(),
    question: z.string(),
    provider: DecisionProvider,
    model: z.string(),
    distribution: z.record(z.string(), z.number()),
    chosen: z.string(),
    confidence: z.number(),
    score: z.number().nullable(),
    band: DecisionBand,
    outcome: z.string().nullable(),
    outcome_at: Timestamp.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "ClassifierDecisionRow" });

export const DecisionListQuery = z.object({
  membership_id: Uuid.optional(),
  run_id: Uuid.optional(),
  rubric_id: Uuid.optional(),
  band: DecisionBand.optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
});
export const DecisionList = z
  .object({ data: z.array(ClassifierDecisionRow) })
  .meta({ id: "DecisionList" });

export const DecideSettings = z
  .object({
    /** Jev on OpenRouter (masked text leaves the company); off: the local model only. */
    external: z.boolean(),
    /** The classifier may re-class a DE-owned board's `now` comments and `batch` edits. */
    board_gate: z.boolean(),
    /** An active company OpenRouter key exists in Connections › API keys (last four only). */
    openrouter_key: z.object({ present: z.boolean(), last4: z.string().nullable() }),
    /** The company's gateway has a light model for in-house classification. */
    local_available: z.boolean(),
    /** What a `decide` call would use now. */
    provider: DecisionProvider.nullable(),
    jev_model: z.string(),
  })
  .meta({ id: "DecideSettings" });
export type DecideSettings = z.infer<typeof DecideSettings>;

export const DecideSettingsUpdate = z
  .strictObject({ external: z.boolean().optional(), board_gate: z.boolean().optional() })
  .meta({ id: "DecideSettingsUpdate" });

const runSub = (p: string) => `${base}/runs/{id}/${p}`;

export const decideRoutes = {
  "runs.decide": defineRoute({
    ...agents,
    id: "runs.decide",
    method: "post",
    path: runSub("decide"),
    tag: "runs",
    summary: "B5-DECIDE: classify items against a rubric (distribution, top option, band)",
    description:
      "The run's token (MCP `decide`). Jev on OpenRouter when the company switched it on and " +
      "holds an OpenRouter key (text masked: e-mail addresses, phone numbers), else the local " +
      "light model; a Jev failure degrades to the local model. Every answer is logged " +
      "(`classifier_decision`), the cost is booked on the run's seat (`cost.posted`, meter " +
      "`decision:{call_id}`) and the call is an Activity line under the run (`classifier.decided`).",
    access: "run_token",
    params: CompanyItemParams,
    body: DecideRequest,
    idempotency: "optional",
    response: { status: 200, schema: DecideResult },
    errors: ["run_not_owned", "run_state_conflict", "classifier_unavailable"],
    emits: ["classifier.decided", "cost.posted"],
  }),
  "runs.decisionRubrics.list": defineRoute({
    ...agents,
    id: "runs.decisionRubrics.list",
    method: "get",
    path: runSub("decision-rubrics"),
    tag: "runs",
    summary: "B5-DECIDE: the run's DE's rubrics (the newest version of each)",
    access: "run_token",
    params: CompanyItemParams,
    response: { status: 200, schema: DecisionRubricList },
    errors: ["run_not_owned"],
    emits: [],
  }),
  "runs.decisionRubrics.save": defineRoute({
    ...agents,
    id: "runs.decisionRubrics.save",
    method: "post",
    path: runSub("decision-rubrics"),
    tag: "runs",
    summary: "B5-DECIDE: the run's DE saves one of its rubrics (always a new version)",
    access: "run_token",
    params: CompanyItemParams,
    body: DecisionRubricSave,
    idempotency: "optional",
    response: { status: 201, schema: DecisionRubric },
    errors: ["run_not_owned", "run_state_conflict"],
    emits: ["decision_rubric.saved"],
  }),
  "decisionRubrics.list": defineRoute({
    ...agents,
    id: "decisionRubrics.list",
    method: "get",
    path: `${base}/decision-rubrics`,
    tag: "decide",
    summary: "B5-DECIDE: the rubrics of the DEs whose runs the caller may view (newest versions)",
    access: "member",
    params: CompanyParams,
    query: DecisionRubricListQuery,
    response: { status: 200, schema: DecisionRubricList },
    errors: [],
    emits: [],
  }),
  "decisionRubrics.get": defineRoute({
    ...agents,
    id: "decisionRubrics.get",
    method: "get",
    path: `${base}/decision-rubrics/{id}`,
    tag: "decide",
    summary: "B5-DECIDE: one rubric version with the list of its versions",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: DecisionRubricDetail },
    errors: [],
    emits: [],
  }),
  "decisionRubrics.edit": defineRoute({
    ...agents,
    id: "decisionRubrics.edit",
    method: "post",
    path: `${base}/decision-rubrics/{id}/versions`,
    tag: "decide",
    summary: "B5-DECIDE: a person edits a DE's rubric (a new version of it)",
    access: "member",
    params: CompanyItemParams,
    body: DecisionRubricEdit,
    idempotency: "optional",
    response: { status: 201, schema: DecisionRubric },
    errors: [],
    emits: ["decision_rubric.saved"],
  }),
  "decisions.list": defineRoute({
    ...agents,
    id: "decisions.list",
    method: "get",
    path: `${base}/decisions`,
    tag: "decide",
    summary: "B5-DECIDE: logged classifier answers of the DEs whose runs the caller may view",
    access: "member",
    params: CompanyParams,
    query: DecisionListQuery,
    response: { status: 200, schema: DecisionList },
    errors: [],
    emits: [],
  }),
  "decide.settings.get": defineRoute({
    ...agents,
    id: "decide.settings.get",
    method: "get",
    path: `${base}/decide/settings`,
    tag: "decide",
    summary: "B5-DECIDE: the classifier switches and what is available",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: DecideSettings },
    errors: [],
    emits: [],
  }),
  "decide.settings.update": defineRoute({
    ...agents,
    id: "decide.settings.update",
    method: "put",
    path: `${base}/decide/settings`,
    tag: "decide",
    summary: "B5-DECIDE: turn Jev (external) and the board gate on or off",
    access: "manager",
    params: CompanyParams,
    body: DecideSettingsUpdate,
    response: { status: 200, schema: DecideSettings },
    errors: [],
    emits: ["company.updated"],
  }),
} as const;
